import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Play, Lock, Flame, CheckCircle2, ChevronRight, Eye, Crown,
  Plus, Edit, Trash2, X, Save
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { timeAgo } from "@/lib/time";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import AdSlot from "@/components/AdSlot";

export default function Home() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [recent, setRecent] = useState([]);
  const [popular, setPopular] = useState([]);
  const [routes, setRoutes] = useState([]);

  const reload = async () => {
    api.get("/videos", { params: { limit: 12 } }).then((r) => setRecent(r.data)).catch(() => {});
    api.get("/videos", { params: { sort: "views", limit: 12 } }).then((r) => setPopular(r.data)).catch(() => {});
    api.get("/routes").then((r) => setRoutes(r.data)).catch(() => {});
  };
  useEffect(() => { reload(); }, []);

  return (
    <div className="text-white" data-testid="home-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 space-y-16">

        {/* ===== Recent Videos ===== */}
        <Section title="Latest Videos" icon={Play} link="/videos">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {recent.map((v) => <HomeVideoCard key={v.id} video={v} />)}
          </div>
        </Section>

        <AdSlot zoneId="5975716" className="eas6a97888e6" />

        {/* ===== Most Popular ===== */}
        <Section title="Most Popular" icon={Eye} link="/videos">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {popular.map((v) => <HomeVideoCard key={v.id} video={v} />)}
          </div>
        </Section>

        <AdSlot zoneId="5975718" className="eas6a97888e17" />

        {/* ===== Routes (admin can manage) ===== */}
        <RoutesSection routes={routes} isAdmin={isAdmin} onChange={reload} />

        {/* ===== Video Status Guide ===== */}
        <Section title="Video Status Guide" icon={Flame}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatusCard color="#E50914" Icon={Lock} title="Patreon Exclusive" desc="Available only for Patreon supporters." />
            <StatusCard color="#FF9F0A" Icon={Flame} title="Unlock In Progress" desc="On YouTube but not unlocked yet. Like to help!" />
            <StatusCard color="#32D74B" Icon={CheckCircle2} title="Unlocked" desc="Goal reached! Available for everyone." />
          </div>
        </Section>
      </div>
    </div>
  );
}

/* ============= Sub-components ============= */

function Section({ title, icon: Icon, link, children, subtitle }) {
  return (
    <section>
      <div className="flex items-end justify-between mb-5 gap-4">
        <div>
          <div className="flex items-center gap-2">
            {Icon && (
              <span className="w-7 h-7 rounded-md bg-[#E50914]/10 border border-[#E50914]/30 flex items-center justify-center">
                <Icon className="w-3.5 h-3.5 text-[#E50914]" />
              </span>
            )}
            <h2 className="font-display font-black text-2xl sm:text-3xl">{title}</h2>
          </div>
          {subtitle && <p className="text-sm text-zinc-500 mt-1 max-w-xl">{subtitle}</p>}
        </div>
        {link && (
          <Link to={link} className="text-xs font-semibold text-zinc-400 hover:text-[#E50914] inline-flex items-center gap-1 shrink-0">
            View all <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}

function HomeVideoCard({ video }) {
  const isLocked = video.status === "patreon_exclusive";
  return (
    <Link
      to={`/videos/${video.id}`}
      data-testid={`video-card-${video.id}`}
      className="group block rounded-2xl overflow-hidden bg-[#0e0e0e] border border-white/5 hover:border-[#E50914]/40 transition-all hover-glow"
    >
      <div className="relative aspect-video overflow-hidden">
        <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        <div className="absolute top-2 left-2"><StatusBadge status={video.status} /></div>
        {isLocked ? (
          <div className="absolute inset-0 backdrop-blur-md bg-black/40 flex items-center justify-center">
            <Lock className="w-7 h-7 text-[#E50914]" strokeWidth={2.5} />
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-12 h-12 rounded-full bg-[#E50914] flex items-center justify-center shadow-[0_0_24px_rgba(229,9,20,0.6)]">
              <Play className="w-5 h-5 text-white fill-white" />
            </div>
          </div>
        )}
      </div>
      <div className="p-4 space-y-2">
        <h3 className="font-display font-bold text-sm sm:text-base text-white group-hover:text-[#E50914] transition-colors line-clamp-2">{video.title}</h3>
        <div className="flex items-center gap-3 text-xs text-zinc-400">
          <span className="flex items-center gap-1.5"><Eye className="w-3 h-3" /> {(video.views || 0).toLocaleString()} views</span>
          <span className="text-zinc-600">·</span>
          <span>{timeAgo(video.publish_date)}</span>
        </div>
      </div>
    </Link>
  );
}

function StatusCard({ color, Icon, title, desc }) {
  return (
    <div className="glass rounded-2xl p-5">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center border mb-3" style={{ background: `${color}1a`, borderColor: `${color}55`, color }}>
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="font-display font-bold text-lg" style={{ color }}>{title}</h3>
      <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{desc}</p>
    </div>
  );
}

const emptyRoute = { id: "", name: "", description: "", character_image: "", order: 99 };

function RoutesSection({ routes, isAdmin, onChange }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyRoute);

  const openNew = () => { setEditing("new"); setForm(emptyRoute); };
  const openEdit = (r) => { setEditing(r.id); setForm({ ...emptyRoute, ...r }); };
  const cancel = () => { setEditing(null); setForm(emptyRoute); };

  const submit = async () => {
    try {
      if (editing === "new") {
        if (!form.id || !form.name) { toast.error("ID and name required"); return; }
        await api.post("/admin/routes", { ...form, order: Number(form.order) || 99 });
        toast.success("Route created");
      } else {
        await api.put(`/admin/routes/${editing}`, {
          name: form.name, description: form.description,
          character_image: form.character_image, order: Number(form.order) || 99,
        });
        toast.success("Route updated");
      }
      cancel(); onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete route "${id}"?`)) return;
    try { await api.delete(`/admin/routes/${id}`); toast.success("Route deleted"); onChange(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section data-testid="home-routes-section">
      <div className="flex items-end justify-between mb-5 gap-4">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-md bg-[#E50914]/10 border border-[#E50914]/30 flex items-center justify-center">
            <Crown className="w-3.5 h-3.5 text-[#E50914]" />
          </span>
          <h2 className="font-display font-black text-2xl sm:text-3xl">Routes</h2>
        </div>
        {isAdmin && (
          <button data-testid="admin-add-route" onClick={openNew} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#E50914] hover:bg-[#F6121D] text-white text-sm font-bold">
            <Plus className="w-4 h-4" /> Add route
          </button>
        )}
      </div>

      {editing && isAdmin && (
        <div className="glass-strong rounded-2xl p-5 mb-5 space-y-3" data-testid="route-form">
          <div className="flex items-center justify-between">
            <h4 className="font-display font-bold">{editing === "new" ? "New route" : "Edit route"}</h4>
            <button onClick={cancel} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <input data-testid="rf-id" placeholder="ID (slug, e.g. debbie)" value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })} disabled={editing !== "new"} className="px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm disabled:opacity-60" />
            <input data-testid="rf-name" placeholder="Display name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm" />
            <input data-testid="rf-image" placeholder="Character image URL" value={form.character_image} onChange={(e) => setForm({ ...form, character_image: e.target.value })} className="px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm md:col-span-2" />
            <input data-testid="rf-order" type="number" placeholder="Order" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm" />
          </div>
          <textarea data-testid="rf-desc" placeholder="Description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm" />
          <div className="flex justify-end">
            <button data-testid="rf-save" onClick={submit} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#E50914] hover:bg-[#F6121D] text-white font-bold text-sm">
              <Save className="w-4 h-4" /> Save
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        {routes.map((r) => (
          <div key={r.id} className="group relative rounded-2xl overflow-hidden border border-white/5 hover:border-[#E50914]/40 transition-all bg-[#0e0e0e]">
            <Link to={`/routes/${r.id}`} data-testid={`home-route-${r.id}`}>
              <div className="relative aspect-[264/507] overflow-hidden bg-[#0e0e0e]">
                <img src={r.character_image} alt={r.name} className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 space-y-2">
                  <div className="flex items-baseline justify-between gap-2">
                    <h3 className="font-display font-black text-base group-hover:text-[#E50914] transition-colors">{r.name}</h3>
                    <span className="text-[10px] text-zinc-300 font-bold">{r.video_count} vids</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#E50914] to-[#F6121D]" style={{ width: `${r.progress}%` }} />
                    </div>
                    <span className="text-[10px] font-bold text-zinc-200">{r.progress}%</span>
                  </div>
                </div>
              </div>
            </Link>
            {isAdmin && (
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button data-testid={`admin-edit-route-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 rounded-md bg-black/70 backdrop-blur text-white hover:bg-[#E50914]"><Edit className="w-3.5 h-3.5" /></button>
                <button data-testid={`admin-del-route-${r.id}`} onClick={() => remove(r.id)} className="p-1.5 rounded-md bg-black/70 backdrop-blur text-[#E50914] hover:bg-[#E50914] hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
